/**
 * Unified Timeline — one provenance-stamped activity stream per record.
 *
 * WHY: the auditor flagged that record activity was passive and
 * single-source — emails, meetings, tasks, tickets, notes, field changes
 * and agent actions each lived in their own silo, so the record page never
 * told the whole story. This module is the PURE merge layer: it normalizes
 * every silo into ONE `TimelineEntry` shape, stamps each entry with its
 * actor (human vs agent) and — for agent work — inline provenance
 * (source, confidence, ledger seal), sorts newest-first, and de-dupes.
 *
 * Deliberately pure: no store, no firebase, no fetch. Callers (the record
 * panel) pass the raw silo arrays in; this module never fabricates an
 * entry — an empty merge honestly means nothing happened.
 */

// ---------------------------------------------------------------------------
// Schema
// ---------------------------------------------------------------------------

/** Every silo the unified stream merges. */
export type TimelineSource =
  | 'email'
  | 'meeting'
  | 'task'
  | 'ticket'
  | 'note'
  | 'agent'
  | 'field-change';

/** One normalized row of the unified stream. */
export type TimelineEntry = {
  id: string;
  /** Epoch millis (UTC) — the sort key. */
  at: number;
  source: TimelineSource;
  title: string;
  body?: string;
  actor: { kind: 'human' | 'agent'; id: string };
  /** Inline WHY for agent-authored entries: origin, confidence, ledger seal. */
  provenance?: { source: string; confidence?: number; ledgerSeal?: string };
  recordId: string;
  refs?: { type: string; id: string }[];
};

/** Loose raw row — every silo feeds partial, defensive data. */
type Raw = Record<string, unknown>;

/**
 * The seven silos {@link mergeTimeline} accepts. All optional-safe, and
 * deliberately loose (`unknown[]`) so callers can pass their typed silo
 * arrays (EmailEntry[], ProjectTask[], …) without adapter casts — every
 * row is defensively re-validated inside the merge.
 */
export interface TimelineInputs {
  emails?: unknown[];
  meetings?: unknown[];
  tasks?: unknown[];
  tickets?: unknown[];
  notes?: unknown[];
  fieldChanges?: unknown[];
  agentActions?: unknown[];
}

// ---------------------------------------------------------------------------
// Parsing helpers — defensive against partial rows
// ---------------------------------------------------------------------------

/** Epoch millis from `at` (number ms or ISO string), else null. */
function toMs(v: unknown): number | null {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string') {
    const t = Date.parse(v);
    if (!Number.isNaN(t)) return t;
  }
  return null;
}

/** Row timestamp: `at`, falling back to `dueAt`/`dueDate`/`createdAt`. */
function rowMs(row: Raw): number | null {
  return toMs(row.at) ?? toMs(row.dueAt) ?? toMs(row.dueDate) ?? toMs(row.createdAt);
}

function str(v: unknown): string | undefined {
  return typeof v === 'string' && v.trim() !== '' ? v : undefined;
}

/** Coerce a row's actor; falls back to the given default. */
function rowActor(row: Raw, fallback: TimelineEntry['actor']): TimelineEntry['actor'] {
  const a = row.actor;
  if (typeof a === 'object' && a !== null) {
    const o = a as Raw;
    const kind = o.kind === 'agent' ? 'agent' : o.kind === 'human' ? 'human' : null;
    const id = str(o.id);
    if (kind && id) return { kind, id };
  }
  if (typeof a === 'string' && a.trim() !== '') return { ...fallback, id: a };
  return fallback;
}

/** Coerce an inline provenance object; undefined when nothing governed. */
function rowProvenance(row: Raw, defaultSource: string): TimelineEntry['provenance'] {
  const p = (typeof row.provenance === 'object' && row.provenance !== null
    ? (row.provenance as Raw)
    : {}) as Raw;
  const source = str(p.source);
  const ledgerSeal = str(p.ledgerSeal) ?? str(row.ledgerSeal);
  const confRaw = p.confidence ?? row.confidence;
  const confidence =
    typeof confRaw === 'number' && Number.isFinite(confRaw)
      ? Math.min(1, Math.max(0, confRaw))
      : undefined;
  if (!source && !ledgerSeal && confidence === undefined) return undefined;
  return { source: source ?? defaultSource, confidence, ledgerSeal };
}

/** Coerce `refs` into typed record references, dropping malformed items. */
function rowRefs(row: Raw): TimelineEntry['refs'] {
  if (!Array.isArray(row.refs)) return undefined;
  const refs = row.refs
    .filter((r): r is Raw => typeof r === 'object' && r !== null)
    .flatMap((r) => {
      const type = str(r.type);
      const id = str(r.id);
      return type && id ? [{ type, id }] : [];
    });
  return refs.length > 0 ? refs : undefined;
}

// ---------------------------------------------------------------------------
// Normalizers — one per silo
// ---------------------------------------------------------------------------

type Partial_ = Omit<TimelineEntry, 'recordId'> | null;

function fromEmail(row: Raw, at: number): Partial_ {
  const subject = str(row.subject) ?? str(row.title);
  if (!subject) return null;
  const dir = row.direction === 'in' ? '←' : '→';
  const who = row.direction === 'in' ? str(row.from) : str(row.to);
  return {
    id: str(row.id) ?? `email-${at}-${subject}`,
    at,
    source: 'email',
    title: `${dir} ${subject}`,
    body: [str(row.preview), who ? `with ${who}` : undefined].filter(Boolean).join(' · ') || undefined,
    actor: rowActor(row, { kind: 'human', id: str(row.from) ?? 'you' }),
  };
}

function fromMeeting(row: Raw, at: number): Partial_ {
  const title = str(row.title);
  if (!title) return null;
  const actions = Array.isArray(row.actionItems)
    ? (row.actionItems as unknown[]).filter((a): a is string => typeof a === 'string')
    : [];
  return {
    id: str(row.id) ?? `meeting-${at}-${title}`,
    at,
    source: 'meeting',
    title,
    body:
      [str(row.summary), actions.length ? `Next: ${actions.join('; ')}` : undefined]
        .filter(Boolean)
        .join(' · ') || undefined,
    actor: rowActor(row, { kind: 'human', id: str(row.source) ?? 'meeting' }),
  };
}

function fromTask(row: Raw, at: number): Partial_ {
  const title = str(row.title);
  if (!title) return null;
  return {
    id: str(row.id) ?? `task-${at}-${title}`,
    at,
    source: 'task',
    title,
    body: [str(row.status), str(row.notes)].filter(Boolean).join(' · ') || undefined,
    actor: rowActor(row, { kind: 'human', id: str(row.assignee) ?? 'unassigned' }),
  };
}

function fromTicket(row: Raw, at: number): Partial_ {
  const title = str(row.title) ?? str(row.subject);
  if (!title) return null;
  return {
    id: str(row.id) ?? `ticket-${at}-${title}`,
    at,
    source: 'ticket',
    title,
    body: [str(row.status), str(row.priority)].filter(Boolean).join(' · ') || undefined,
    actor: rowActor(row, { kind: 'human', id: str(row.owner) ?? str(row.createdBy) ?? 'unclaimed' }),
  };
}

function fromNote(row: Raw, at: number): Partial_ {
  const text = str(row.text) ?? str(row.title) ?? str(row.summary);
  if (!text) return null;
  return {
    id: str(row.id) ?? `note-${at}-${text}`,
    at,
    source: 'note',
    title: text,
    actor: rowActor(row, { kind: 'human', id: 'you' }),
  };
}

/** Honest '—' for empty prior values in a field-change line. */
function show(v: unknown): string {
  if (v === null || v === undefined) return '—';
  const s = typeof v === 'string' ? v : String(v);
  return s.trim() === '' ? '—' : s;
}

function fromFieldChange(row: Raw, at: number): Partial_ {
  const attr = str(row.attr);
  if (!attr) return null;
  const title = `${attr}: ${show(row.from)} → ${show(row.to)}`;
  return {
    id: str(row.id) ?? `field-${at}-${attr}`,
    at,
    source: 'field-change',
    title,
    actor: rowActor(row, { kind: 'human', id: 'you' }),
    provenance: rowProvenance(row, 'ledger'),
  };
}

function fromAgentAction(row: Raw, at: number): Partial_ {
  const title = str(row.summary) ?? str(row.title);
  if (!title) return null;
  return {
    id: str(row.id) ?? `agent-${at}-${title}`,
    at,
    source: 'agent',
    title,
    body: str(row.detail) ?? str(row.kind),
    actor: { kind: 'agent', id: str(row.agentId) ?? str(row.agentName) ?? 'argo' },
    provenance: rowProvenance(row, 'agent'),
    refs: rowRefs(row),
  };
}

// ---------------------------------------------------------------------------
// mergeTimeline
// ---------------------------------------------------------------------------

/**
 * Merge the seven silos into one provenance-stamped stream for a record:
 *   • drops rows with no parseable timestamp or no text (defensive);
 *   • de-dupes by entry id — first occurrence wins;
 *   • sorts newest-first (ties broken by id for a stable render order).
 * Pure — inputs are never mutated; empty/missing inputs yield [].
 */
export function mergeTimeline(
  inputs: TimelineInputs | null | undefined,
  opts: { recordId: string },
): TimelineEntry[] {
  const safe = (rows: unknown): Raw[] =>
    Array.isArray(rows) ? rows.filter((r): r is Raw => typeof r === 'object' && r !== null) : [];

  const lanes: [Raw[], (row: Raw, at: number) => Partial_][] = [
    [safe(inputs?.emails), fromEmail],
    [safe(inputs?.meetings), fromMeeting],
    [safe(inputs?.tasks), fromTask],
    [safe(inputs?.tickets), fromTicket],
    [safe(inputs?.notes), fromNote],
    [safe(inputs?.fieldChanges), fromFieldChange],
    [safe(inputs?.agentActions), fromAgentAction],
  ];

  const seen = new Set<string>();
  const out: TimelineEntry[] = [];
  for (const [rows, normalize] of lanes) {
    for (const row of rows) {
      const at = rowMs(row);
      if (at === null) continue;
      const partial = normalize(row, at);
      if (!partial) continue;
      if (seen.has(partial.id)) continue; // de-dupe by id — first wins
      seen.add(partial.id);
      out.push({ ...partial, recordId: opts.recordId });
    }
  }

  out.sort((a, b) => (b.at - a.at) || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
  return out;
}

// ---------------------------------------------------------------------------
// Lenses over a merged stream
// ---------------------------------------------------------------------------

/**
 * Keep only entries whose source is in `sources`. An EMPTY selection means
 * "no filter" — the whole stream — so filter chips can all be toggled off
 * without blanking the panel. Pure; order preserved.
 */
export function filterTimeline(
  entries: TimelineEntry[],
  sources: TimelineSource[],
): TimelineEntry[] {
  if (sources.length === 0) return entries.slice();
  const keep = new Set(sources);
  return entries.filter((e) => keep.has(e.source));
}

/**
 * Bucket entries by UTC calendar day (`YYYY-MM-DD`). Within-day order is
 * preserved as given (a desc stream stays desc per bucket); buckets appear
 * in first-seen order, i.e. newest day first for a desc stream. Pure.
 */
export function groupByDay(
  entries: TimelineEntry[],
): { day: string; entries: TimelineEntry[] }[] {
  const order: string[] = [];
  const byDay = new Map<string, TimelineEntry[]>();
  for (const e of entries) {
    const day = new Date(e.at).toISOString().slice(0, 10);
    let bucket = byDay.get(day);
    if (!bucket) {
      bucket = [];
      byDay.set(day, bucket);
      order.push(day);
    }
    bucket.push(e);
  }
  return order.map((day) => ({ day, entries: byDay.get(day)! }));
}

/** The most recent entry timestamp, or null for an empty stream. Pure. */
export function lastTouch(entries: TimelineEntry[]): number | null {
  let max: number | null = null;
  for (const e of entries) if (max === null || e.at > max) max = e.at;
  return max;
}

/**
 * The NEXT scheduled entry: the future entry (at > now) closest to now —
 * e.g. the upcoming task due date — or null when nothing is scheduled. Pure.
 */
export function nextScheduled(entries: TimelineEntry[], now: number): TimelineEntry | null {
  let best: TimelineEntry | null = null;
  for (const e of entries) {
    if (e.at <= now) continue;
    if (best === null || e.at < best.at) best = e;
  }
  return best;
}
